import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { RequestQuote } from '@/ui-kit/components/RequestQuote/RequestQuote';
import { QrCodeData, Sheet, TooltipData, useSheetRouter } from '../SheetRouter/SheetRouterContext';
import BottomSheet from '../BottomSheet/BottomSheet';
import ThemeDefinition from '../../domain/ThemeDefinition';
import { UiBuilder } from '@/UiBuilder/UiBuilder';
import { useBootstrapper } from '../../contexts/BootstrapperContext';

import routerStyles from './SheetRouter.module.css';
import { useConfigurator } from '@/components/Configurator/ConfiguratorContext';
import { MenuListItem } from '@/ui-kit/components/MenuList/MenuListItem';
import { isWebArEnabled } from '@/ui-kit/domain/UiSpecification';
import { WebArMenuItem } from '@/ui-kit/components/WebArControl/WebArMenuItem';
import { QrCodeDialog } from '@/ui-kit/components/WebArControl/QrCodeDialog';
import { observer } from 'mobx-react';
import { PdfDownloadMenuItem } from '@/ui-kit/components/PdfDownloadButton/PdfDownloadMenuItem';
import { useGetShareLink } from '@/ui-kit/hooks';
import { copyToClipboard } from '@/helpers/utils';
import { Button, Input, Typography } from '@canvas-logic/clp-shared-ui';

type MainMenuProps = {
  level: number;
  onClose: () => void;
  onContentResize?: (height: number) => void;
  maxContentHeight?: number;
};

const MainMenu = ({ level, onClose, maxContentHeight }: MainMenuProps) => {
  const { sheets } = useSheetRouter();
  const { bootstrapper } = useBootstrapper();

  if (!bootstrapper) {
    return null;
  }

  return (
    <BottomSheet
      variant="main"
      onClose={onClose}
      level={level}
      backdrop={sheets.length === level}
      maxContentHeight={maxContentHeight}
    >
      <BottomSheet.Content>
        <div className={'cp-mobile'}>
          <div className={'cp-mobile__header'}> Configure</div>

          <UiBuilder
            configuratorUI={bootstrapper.configuratorUI}
            bootstrapper={bootstrapper}
            configuration={bootstrapper.productVariant}
          />
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  );
};

const RequestQuoteSheet = ({ level, onClose, maxContentHeight }: MainMenuProps) => {
  const { sheets } = useSheetRouter();
  const { bootstrapper } = useBootstrapper();
  const { t } = useTranslation();

  if (!bootstrapper?.contactUsService) {
    return null;
  }

  return (
    <BottomSheet onClose={onClose} level={level} backdrop={sheets.length === level} maxContentHeight={maxContentHeight}>
      <BottomSheet.Content title={t(`form.request-quote.title.contactDetails`)}>
        <RequestQuote contactUsService={bootstrapper.contactUsService} createLink={bootstrapper.createLink} />
      </BottomSheet.Content>
    </BottomSheet>
  );
};

const ShareLinkSheet = ({ level, onClose, maxContentHeight }: MainMenuProps) => {
  const { sheets } = useSheetRouter();
  const { t: tTranslation } = useTranslation('translation');
  const { t: tShareLink } = useTranslation('translation', { keyPrefix: 'configuratorPage.shareLink' });
  const { state, requestLink, link, reset } = useGetShareLink();
  const [showCopyMessage, setShowCopyMessage] = useState(false);

  useEffect(() => {
    void requestLink();

    return () => {
      reset();
    };
  }, [requestLink, reset]);

  const handleCopyLink = async () => {
    if (!link) {
      return;
    }

    setShowCopyMessage(true);
    setTimeout(() => {
      setShowCopyMessage(false);
    }, 3000);
    await copyToClipboard(link);
  };

  return (
    <BottomSheet onClose={onClose} level={level} backdrop={sheets.length === level} maxContentHeight={maxContentHeight}>
      <BottomSheet.Content title={<Typography variant="heading4">{tShareLink('title')}</Typography>}>
        <div className={routerStyles.shareLinkContent}>
          <Typography variant="paragraph-md" color="description">
            {tShareLink('description')}
          </Typography>
          <div className={routerStyles.shareLinkControls}>
            <Input
              label=""
              placeholder={state === 'loading' ? 'Generating link...' : undefined}
              defaultValue={link}
              readOnly
            />
            {showCopyMessage ? (
              <Button variant="ghost" disabled>
                {tTranslation('buttons.linkCopied')}
              </Button>
            ) : (
              <Button variant="primary" onClick={handleCopyLink}>
                {tShareLink('copyLink')}
              </Button>
            )}
          </div>
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  );
};

const Actions = observer(({ level, onClose, maxContentHeight }: MainMenuProps) => {
  const configurator = useConfigurator();
  const { sheets, openShareLink } = useSheetRouter();
  const { bootstrapper } = useBootstrapper();
  const { t } = useTranslation('translation');

  if (!bootstrapper || !configurator) {
    return null;
  }

  return (
    <BottomSheet
      onClose={onClose}
      level={level}
      backdrop={sheets.length === level}
      maxContentHeight={maxContentHeight}
      noClose
    >
      <BottomSheet.Content>
        <div className={routerStyles.actionsContent}>
          <MenuListItem
            icon="info"
            label={bootstrapper.showRuler ? t('buttons.hideAdditionalInfo') : t('buttons.showAdditionalInfo')}
            onClick={() => bootstrapper.switchRuler()}
          />
          {isWebArEnabled(configurator.configuratorUI) && (
            <WebArMenuItem api={bootstrapper.api} label={t('buttons.viewInAr')} />
          )}
          <MenuListItem icon="share" label={t('buttons.copyLink')} onClick={openShareLink} />
          {bootstrapper.isInCatalog && <PdfDownloadMenuItem label={t('buttons.generatePdf')} />}
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  );
});

type Props = {
  level: number;
  sheet: Sheet;
};

const SheetRender = ({ sheet, level }: Props) => {
  const { close, sheets, setSheetHeight, minimalSheetHeight } = useSheetRouter();

  switch (sheet.type) {
    case 'menu': {
      return (
        <MainMenu
          level={level}
          onClose={() => close(sheet.id)}
          onContentResize={(height) => setSheetHeight(sheet.id, height)}
          maxContentHeight={minimalSheetHeight}
        />
      );
    }
    case 'contact-us': {
      return (
        <RequestQuoteSheet
          key={sheet.id}
          level={level}
          onClose={() => close(sheet.id)}
          onContentResize={(height) => setSheetHeight(sheet.id, height)}
          maxContentHeight={minimalSheetHeight}
        />
      );
    }
    case 'actions': {
      return (
        <Actions
          key={sheet.id}
          level={level}
          onClose={() => close(sheet.id)}
          onContentResize={(height) => setSheetHeight(sheet.id, height)}
          maxContentHeight={minimalSheetHeight}
        />
      );
    }
    case 'tooltip': {
      const data = sheet.arguments as TooltipData;

      return (
        <BottomSheet
          onClose={() => close(sheet.id)}
          backdrop={sheets.length === level}
          level={level}
          onContentResize={(height) => setSheetHeight(sheet.id, height)}
          maxContentHeight={minimalSheetHeight}
        >
          <BottomSheet.Content>
            <div className={classNames(routerStyles.tooltipContent, ThemeDefinition.layout.controls)}>
              <Typography className={classNames(ThemeDefinition.layout.header)} variant="heading4" component="h1">
                {data.title}
              </Typography>
              <Typography variant={'paragraph-md'}>{data.description}</Typography>
            </div>
          </BottomSheet.Content>
        </BottomSheet>
      );
    }
    case 'qr-code': {
      const data = sheet.arguments as QrCodeData;

      return (
        <BottomSheet
          onClose={() => close(sheet.id)}
          backdrop={sheets.length === level}
          level={level}
          onContentResize={(height) => setSheetHeight(sheet.id, height)}
          maxContentHeight={minimalSheetHeight}
        >
          <BottomSheet.Content>
            <div className={classNames(routerStyles.tooltipContent, ThemeDefinition.layout.controls)}>
              <QrCodeDialog className={routerStyles.qrCodeContent} link={data.link} />
            </div>
          </BottomSheet.Content>
        </BottomSheet>
      );
    }
    case 'share-link': {
      return (
        <ShareLinkSheet
          key={sheet.id}
          level={level}
          onClose={() => close(sheet.id)}
          onContentResize={(height) => setSheetHeight(sheet.id, height)}
          maxContentHeight={minimalSheetHeight}
        />
      );
    }
  }
};

const SheetRouter = () => {
  const { sheets } = useSheetRouter();

  return (
    <>
      {sheets.map((sheet, index) => (
        <SheetRender key={sheet.id} sheet={sheet} level={sheets.length - index} />
      ))}
    </>
  );
};

export default SheetRouter;
